import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addItem, delItem } from "../redux/action/index";
import { sendRequest } from "../ApiRequest";
import { BASE_URL } from "../URL/constants";
import MessageModal from "./Common/MessageModal";
import useMessageModal from "../hooks/useMessageModal";
import {
  FaHeart,
  FaRegHeart,
  FaMinus,
  FaPlus,
  FaChevronRight,
  FaBarcode,
  FaLayerGroup,
  FaBox,
} from "react-icons/fa";
import "./ProductDetail.css";
import {
  addToWishlist,
  removeFromWishlist,
  checkWishlistStatus,
} from "../components/wishlist";
import getCurrentUser from "../components/common/GetCurrentUser";
import SignupModal from "./buttons/SignupModal";
import CheckoutHandler from "./common/CheckoutHandler";
import useCurrentUser from "./common/UseCurrentUser";


const ProductDetail = () => {
  const [cartBtn, setCartBtn] = useState("ADD TO CART");
  const [wishlistStatus, setWishlistStatus] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [availableStock, setAvailableStock] = useState(0);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [mainImage, setMainImage] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [variantPrice, setVariantPrice] = useState(null);
  const { loginUser, currentUser } = getCurrentUser();
  const { isLoggedIn, loginUser: loginUserHook } = useCurrentUser();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingCheckoutItem, setPendingCheckoutItem] = useState(null);

  // SignupModal states
  const [activeTab, setActiveTab] = useState("login");
  const [showOtpLogin, setShowOtpLogin] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const { modalState, showError, showWarning, closeModal } = useMessageModal();

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchProductDetail = async () => {
      try {
        const response = await sendRequest(
          "GET",
          `${BASE_URL}/v1/public_site/menus/item_detail?id=${id}`
        );
        setProduct(response.data.data);
        // Set main image from item_images array or fallback to item_url or default image
        const images = response.data.data.item_images || [];
        console.log('API Response - item_images:', images);
        console.log('API Response - item_url:', response.data.data.item_url);
        console.log('API Response - full data:', response.data.data);
        
        if (images.length > 0) {
          console.log('Setting main image from item_images[0]:', images[0]);
          setMainImage(images[0]);
          setCurrentImageIndex(0); // Reset to first image
        } else if (response.data.data.item_url) {
          console.log('Setting main image from item_url:', response.data.data.item_url);
          setMainImage(response.data.data.item_url);
          setCurrentImageIndex(0);
        } else {
          console.log('Setting default image');
          setMainImage("/assets/images/products/p.png");
          setCurrentImageIndex(0);
        }
        const productData = response.data.data;
        
        // Check if variants are enabled and available
        if (productData.variant_enable && productData.variants && productData.variants.length > 0) {
          // If variants exist, select the first one by default
          const firstVariant = productData.variants[0];
          setSelectedVariant(firstVariant);
          setAvailableStock(firstVariant.available_stock || 0);
          setVariantPrice(firstVariant.sale_price || firstVariant.price || productData.sales_price);
        } else {
          // No variants, use item's stock and price
          setAvailableStock(productData.stock_quantity || productData.available_quantity || 0);
          setVariantPrice(null);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching product details:", error);
        setError(error.message);
        setLoading(false);
      }
    };
    fetchProductDetail();
  }, [id]);

  useEffect(() => {
    const checkCartStatus = async () => {
      const currentUser = JSON.parse(localStorage.getItem("userDetails"));
      const cartState = JSON.parse(localStorage.getItem("cartState")) || {};
      const guestCartRaw = JSON.parse(localStorage.getItem("guestCart")) || [];
      
      // Handle both array and object formats of guestCart
      let guestCart;
      if (Array.isArray(guestCartRaw)) {
        guestCart = guestCartRaw;
      } else {
        guestCart = Object.values(guestCartRaw);
      }
      
      if (!currentUser) {
        // For guest users, check if item exists in guest cart
        const isInGuestCart = guestCart.some(item => item.id === parseInt(id) || item.item_id === parseInt(id));
        setCartBtn(isInGuestCart ? "REMOVE FROM CART" : "ADD TO CART");
      } else {
        // For logged-in users, check if item exists in server cart
        try {
          const response = await sendRequest(
            "GET",
            `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart`,
            null,
            { Authorization: currentUser.data.token }
          );
          const cartItems = response.data.cart_items || [];
          const isInCart = cartItems.some(item => item.item_id === parseInt(id));
          setCartBtn(isInCart ? "REMOVE FROM CART" : "ADD TO CART");
          
          // Update cartState to match server state
          if (isInCart && !cartState[id]) {
            cartState[id] = true;
            localStorage.setItem("cartState", JSON.stringify(cartState));
          } else if (!isInCart && cartState[id]) {
            delete cartState[id];
            localStorage.setItem("cartState", JSON.stringify(cartState));
          }
        } catch (error) {
          console.error("Error checking cart status:", error);
          // Fallback to cartState
          setCartBtn(cartState[id] ? "REMOVE FROM CART" : "ADD TO CART");
        }
      }
    };
    
    checkCartStatus();
    
    // Cleanup function to reset cart button when component unmounts
    return () => {
      setCartBtn("ADD TO CART");
    };
  }, [id, currentUser]);

  const token = currentUser?.data?.token;
  useEffect(() => {
    const fetchStatus = async () => {
      if (!id || !token) return;

      try {
        const isInWishlist = await checkWishlistStatus(id, token);
        console.log("Wishlist status:", isInWishlist);
        setWishlistStatus(isInWishlist);
      } catch (err) {
        console.error("Error checking wishlist status:", err);
      }
    };

    fetchStatus();
  }, [id, token]);

  const handleCart = async (product) => {
    // Check if variant is required but not selected
    if (product.variant_enable && product.variants && product.variants.length > 0 && !selectedVariant) {
      showError("Please select a variant before adding to cart.");
      return;
    }

    // Check if quantity exceeds available stock (skip validation for unlimited items with -1)
    if (availableStock !== -1 && quantity > availableStock) {
      showError(`Cannot add ${quantity} items. Only ${availableStock} items available in stock.`);
      return;
    }

    const currentUser = JSON.parse(localStorage.getItem("userDetails"));
    let cartState = JSON.parse(localStorage.getItem("cartState")) || {};
    let guestCartRaw = JSON.parse(localStorage.getItem("guestCart")) || [];
    
    // Handle both array and object formats of guestCart
    let guestCart;
    if (Array.isArray(guestCartRaw)) {
      guestCart = guestCartRaw;
    } else {
      guestCart = Object.values(guestCartRaw);
    }
    
    // Create cart item with variant information
    const cartItemKey = selectedVariant ? `${product.id}_${selectedVariant.id}` : product.id;
    const isInCart = cartState[cartItemKey];

    // Prepare product data with variant info
    const productWithVariant = {
      ...product,
      quantity,
      selected_variant: selectedVariant ? {
        id: selectedVariant.id,
        variant_detail: selectedVariant.variant_detail,
        sku: selectedVariant.sku,
        price: variantPrice || selectedVariant.price,
      } : null
    };

    if (!currentUser || !currentUser.data || !currentUser.data.token || !currentUser.data.customer) {
      if (!isInCart) {
        const updatedCart = [...guestCart, productWithVariant];
        localStorage.setItem("guestCart", JSON.stringify(updatedCart));
        dispatch(addItem(productWithVariant));
        setCartBtn("REMOVE FROM CART");
        cartState[cartItemKey] = true;
      } else {
        const guestCartArray = Object.values(guestCart);
        const updatedCart = guestCartArray.filter(
          (item) => {
            if (selectedVariant) {
              return !(item.id === product.id && item.selected_variant?.id === selectedVariant.id);
            }
            return item.id !== product.id;
          }
        );
        localStorage.setItem("guestCart", JSON.stringify(updatedCart));
        dispatch(delItem(productWithVariant));
        setCartBtn("ADD TO CART");
        delete cartState[cartItemKey];
      }
      localStorage.setItem("cartState", JSON.stringify(cartState));
      // Dispatch cart-updated event to update cart count immediately
      window.dispatchEvent(new Event('cart-updated'));
      return;
    }

    const token = currentUser.data.token;
    const customerId = currentUser.data.customer.id;

    if (cartBtn === "ADD TO CART") {
      const cartData = {
        customer_id: customerId,
        item_id: product.id,
        quantity: quantity,
        variant_option_combination_id: selectedVariant?.id || null,
      };

      try {
        const response = await sendRequest(
          "POST",
          `${BASE_URL}/v1/public_site/customers/${customerId}/cart/cart_items`,
          cartData,
          { Authorization: token }
        );

        if (response.data) {
          dispatch(addItem(productWithVariant));
          setCartBtn("REMOVE FROM CART");
          cartState[cartItemKey] = true;
          localStorage.setItem("cartState", JSON.stringify(cartState));
          // Dispatch cart-updated event to update cart count immediately
          window.dispatchEvent(new Event('cart-updated'));
        } else {
          console.error("Error adding to cart:", response.message);
        }
      } catch (err) {
        console.error("API error:", err);
      }
    } else {
      const response = await sendRequest(
        "DELETE",
        `${BASE_URL}/v1/public_site/customers/${customerId}/cart/cart_items/${product.id}`,
        null,
        { Authorization: token }
      );

      if (response.data) {
        dispatch(delItem(productWithVariant));
        setCartBtn("ADD TO CART");
        delete cartState[cartItemKey];
        localStorage.setItem("cartState", JSON.stringify(cartState));
        // Dispatch cart-updated event to update cart count immediately
        window.dispatchEvent(new Event('cart-updated'));
      } else {
        console.error("Error removing from cart:", response.message);
      }
    }
  };

  const handleWishlist = async () => {
    const token = currentUser?.data?.token;
    if (!token) {
      setError("You need to log in to use the wishlist.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (wishlistStatus) {
        await removeFromWishlist(id, token);
      } else {
        await addToWishlist(id, token);
      }
      setWishlistStatus(!wishlistStatus);
    } catch (error) {
      const message =
        error?.data?.error || "Something went wrong. Please try again.";
      setError(message);

      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantity = (operation) => {
    if (operation === "decrease" && quantity > 1) {
      setQuantity(quantity - 1);
    } else if (operation === "increase") {
      // Handle unlimited stock (-1) or check stock availability
      if (availableStock === -1) {
        // Unlimited stock, allow any quantity
        setQuantity(quantity + 1);
      } else if (availableStock > 0 && quantity >= availableStock) {
        showWarning(`Only ${availableStock} items available in stock.`);
      } else if (availableStock > 0) {
        setQuantity(quantity + 1);
      }
    }
  };

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    setAvailableStock(variant.available_stock || 0);
    setVariantPrice(variant.sale_price || variant.price || product.sales_price);
    // Reset quantity to 1 when variant changes
    setQuantity(1);
  };

  // Get current price (variant price if variant selected, otherwise item price)
  const getCurrentPrice = () => {
    if (selectedVariant && variantPrice) {
      return variantPrice;
    }
    return product?.sales_price || 0;
  };

  // Get current discount
  const getCurrentDiscount = () => {
    if (selectedVariant) {
      // For variants, discount might be calculated differently
      return product?.sales_discount || 0;
    }
    return product?.sales_discount || 0;
  };


  const nextImage = () => {
    if (product?.item_images && product.item_images.length > 0) {
      setCurrentImageIndex((prevIndex) => {
        const nextIndex = prevIndex === product.item_images.length - 1 ? 0 : prevIndex + 1;
        console.log('Next image:', prevIndex, '->', nextIndex, 'URL:', product.item_images[nextIndex]);
        setMainImage(product.item_images[nextIndex]);
        return nextIndex;
      });
    }
  };

  const prevImage = () => {
    if (product?.item_images && product.item_images.length > 0) {
      setCurrentImageIndex((prevIndex) => {
        const prevIndexNew = prevIndex === 0 ? product.item_images.length - 1 : prevIndex - 1;
        console.log('Prev image:', prevIndex, '->', prevIndexNew, 'URL:', product.item_images[prevIndexNew]);
        setMainImage(product.item_images[prevIndexNew]);
        return prevIndexNew;
      });
    }
  };

  const goToImage = (index) => {
    if (product?.item_images && product.item_images.length > 0) {
      console.log('Go to image:', index, 'URL:', product.item_images[index]);
      setCurrentImageIndex(index);
      setMainImage(product.item_images[index]);
    }
  };

  // Effect to control SignupModal
  useEffect(() => {
    const modal = document.getElementById('signupModal');
    if (!modal || typeof window === 'undefined' || !window.bootstrap) {
      return;
    }

    if (showLoginModal) {
      // Check if an instance already exists
      let bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      
      // If instance exists, dispose it first to avoid conflicts
      if (bootstrapModal) {
        bootstrapModal.dispose();
      }
      
      // Create a new modal instance
      bootstrapModal = new window.bootstrap.Modal(modal, {
        backdrop: true,
        keyboard: true
      });
      
      // Show the modal
      bootstrapModal.show();
      
      // Listen for hidden event to reset state
      const handleHidden = () => {
        setShowLoginModal(false);
        modal.removeEventListener('hidden.bs.modal', handleHidden);
      };
      
      modal.addEventListener('hidden.bs.modal', handleHidden);
      
      // Cleanup
      return () => {
        modal.removeEventListener('hidden.bs.modal', handleHidden);
        const instance = window.bootstrap.Modal.getInstance(modal);
        if (instance) {
          instance.hide();
        }
      };
    } else {
      // If showLoginModal is false, make sure modal is hidden
      const instance = window.bootstrap.Modal.getInstance(modal);
      if (instance) {
        instance.hide();
      }
    }
  }, [showLoginModal]);

  const closeSignupModal = () => {
    // Close Bootstrap modal first
    const modal = document.getElementById('signupModal');
    if (modal && typeof window !== 'undefined' && window.bootstrap) {
      const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
      }
    }
    
    // Reset state after a small delay to ensure modal is closed
    setTimeout(() => {
      setShowLoginModal(false);
      setShowOtpLogin(false);
      setPhoneNumber("");
      setOtp("");
      setFirstName("");
      setLastName("");
      setEmail("");
      setActiveTab("login"); // Reset to login tab
    }, 100);
  };

  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      console.log("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/send_otp`,
        { customer: { login: phoneNumber } }
      );

      if (response.data) {
        setShowOtpLogin(true);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      console.log("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/verify_otp`,
        { customer: { login: phoneNumber, otp: otp } }
      );

      if (response.data) {
        // Check if login was successful
        if (response.data.message === "Login successful") {
          // User exists, log them in
          if (!response.data.token) {
            console.error("No token in response:", response.data);
            return;
          }
          
          if (!response.data.customer || !response.data.customer.id) {
            console.error("No customer data in response:", response.data);
            return;
          }
          
          localStorage.setItem("authToken", response.data.token);
          
          // Prepare user data structure that matches what loginUser expects
          const userData = {
            data: {
              customer: response.data.customer,
              token: response.data.token
            }
          };
          
          // Store userDetails with the correct structure
          localStorage.setItem("userDetails", JSON.stringify(userData));
          loginUser(userData);
          loginUserHook(userData);
          
          // Verify login was successful
          const storedUser = JSON.parse(localStorage.getItem("userDetails"));
          if (!storedUser || !storedUser.data || !storedUser.data.customer) {
            console.error("Failed to store user data properly");
            return;
          }
          
          // Dispatch event to refresh header after login
          window.dispatchEvent(new Event('user-logged-in'));
          
          // Close modal properly
          const modalElement = document.getElementById("signupModal");
          if (modalElement && window.bootstrap) {
            const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
            if (modalInstance) {
              modalInstance.hide();
            }
          }
          closeSignupModal();
          
          // After successful login, proceed with checkout if there's a pending item
          if (pendingCheckoutItem) {
            setTimeout(() => {
              // Ensure quantity is explicitly set
              const itemWithQuantity = {
                ...pendingCheckoutItem,
                quantity: pendingCheckoutItem.quantity || quantity
              };
              console.log('After login, proceeding to checkout with quantity:', itemWithQuantity.quantity, 'Item:', itemWithQuantity);
              const { handleCheckout } = CheckoutHandler({
                items: [itemWithQuantity],
                setShowLoginModal: setShowLoginModal,
                navigate,
                dispatch,
                isLoggedIn: true
              });
              handleCheckout();
              setPendingCheckoutItem(null);
            }, 300);
          }
        } else {
          // New user, show signup fields
          setActiveTab("signup");
        }
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !email) {
      console.log("Please fill in all required fields");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/sign_up`,
        {
          customer: {
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phoneNumber,
          }
        }
      );

      if (response.data) {
        // Store authToken separately
        if (response.data.token) {
          localStorage.setItem("authToken", response.data.token);
        }
        
        // Prepare user data structure that matches what loginUser expects
        const userData = {
          data: {
            customer: response.data.customer,
            token: response.data.token
          }
        };
        
        // Store userDetails with the correct structure
        localStorage.setItem("userDetails", JSON.stringify(userData));
        loginUser(userData);
        loginUserHook(userData);
        
        // Dispatch event to refresh header after signup
        window.dispatchEvent(new Event('user-logged-in'));
        
        // Close modal properly
        const modalElement = document.getElementById("signupModal");
        if (modalElement && window.bootstrap) {
          const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
          if (modalInstance) {
            modalInstance.hide();
          }
        }
        closeSignupModal();
        
        // After successful signup, proceed with checkout if there's a pending item
        if (pendingCheckoutItem) {
          setTimeout(() => {
            // Ensure quantity is explicitly set
            const itemWithQuantity = {
              ...pendingCheckoutItem,
              quantity: pendingCheckoutItem.quantity || quantity
            };
            console.log('After signup, proceeding to checkout with quantity:', itemWithQuantity.quantity, 'Item:', itemWithQuantity);
            const { handleCheckout } = CheckoutHandler({
              items: [itemWithQuantity],
              setShowLoginModal: setShowLoginModal,
              navigate,
              dispatch,
              isLoggedIn: true
            });
            handleCheckout();
            setPendingCheckoutItem(null);
          }, 300);
        }
      }
    } catch (error) {
      console.error("Error signing up:", error);
    }
  };






  if (loading) {
    return (
      <div className="container my-5 py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container my-5 py-5 text-center">
        <div className="alert alert-warning">Product not found</div>
      </div>
    );
  }

  return (
    <div className="product-detail-container">
      <div className="container">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="breadcrumb-nav">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <a href="/products">Products</a>
            </li>
            <li className="breadcrumb-item active">{product.cat_name}</li>
          </ol>
        </nav>

        <div className="row product-detail-main">
          {/* Left Column - Product Images */}
          <div className="col-lg-6 product-images-col">
            <div className="product-image-main">
              <div className="image-slider-container">
                <img
                  src={mainImage}
                  alt={product.item_name}
                  className="img-fluid main-product-image"
                  onError={(e) => {
                    e.target.src = "/assets/images/products/p.png";
                  }}
                />
                
                {/* Navigation buttons for multiple images */}
                {product?.item_images && product.item_images.length > 1 && (
                  <>
                    <button 
                      className="slider-nav-btn slider-prev" 
                      onClick={prevImage}
                      aria-label="Previous image"
                    >
                      <FaChevronRight style={{ transform: 'rotate(180deg)' }} />
                    </button>
                    <button 
                      className="slider-nav-btn slider-next" 
                      onClick={nextImage}
                      aria-label="Next image"
                    >
                      <FaChevronRight />
                    </button>
                  </>
                )}
                
                {/* Image counter - only show if there are multiple images */}
                {product?.item_images && product.item_images.length > 1 && (
                  <div className="image-counter">
                    {currentImageIndex + 1} / {product.item_images.length}
                  </div>
                )}
              </div>
              
              {/* Image dots indicator */}
              {product?.item_images && product.item_images.length > 1 && (
                <div className="slider-dots">
                  {product.item_images.map((_, index) => (
                    <button
                      key={index}
                      className={`slider-dot ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => goToImage(index)}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Shop by Variant/Look Section */}
            {product.variant_enable && product.variants && product.variants.length > 0 && (
              <div className="shop-by-variant-section">
                <h6 className="variant-section-title">Shop by Variant/Look</h6>
                <div className="variant-thumbnails">
                  {product.variants.map((variant, index) => {
                    const isSelected = selectedVariant?.id === variant.id;
                    return (
                      <div
                        key={variant.id}
                        className={`variant-thumbnail ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleVariantSelect(variant)}
                      >
                        <img
                          src={product.item_images?.[index] || mainImage || "/assets/images/products/p.png"}
                          alt={`Variant ${index + 1}`}
                          onError={(e) => {
                            e.target.src = "/assets/images/products/p.png";
                          }}
                        />
            </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Barcode Section */}
            {product.bar_code_url && (
              <div className="barcode-section mt-4">
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">
                      <FaBarcode className="me-2" />
                      Product Barcode
                    </h6>
                  </div>
                  <div className="card-body text-center">
                    <img
                      src={product.bar_code_url}
                      alt={`Barcode for ${product.item_name}`}
                      className="barcode-image"
                      style={{ maxWidth: "200px", height: "auto" }}
                    />
                    <p className="barcode-text mt-2">
                      <small className="text-muted">
                        Barcode: {product.bar_code}
                      </small>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Product Details */}
          <div className="col-lg-6 product-details-col">
            {/* Header Section with Category and Title */}
            <div className="product-header-new">
              <span className="product-category-badge">{product.cat_name || 'Product'}</span>
              <h1 className="product-title-new">{product.item_name}</h1>
            </div>

            {/* Pricing Section */}
            <div className="pricing-section-modern">
              <div className="price-display-modern">
                {(() => {
                  const currentPrice = getCurrentPrice();
                  const discount = getCurrentDiscount();
                  const finalPrice = currentPrice - discount;
                  return (
                    <>
                      <div className="price-row">
                        <span className="price-current-modern">₹{finalPrice.toLocaleString()}</span>
                        {discount > 0 && (
                          <>
                            <span className="price-original-modern">₹{currentPrice.toLocaleString()}</span>
                            <span className="discount-badge-modern">₹{discount.toLocaleString()} OFF</span>
                          </>
                        )}
                    </div>
                      <p className="tax-info">Price incl. of all taxes</p>
                    </>
                  );
                })()}
                    </div>
                  </div>

            {/* Variant Selection Section - Modern Style */}
            {product.variant_enable && product.variants && product.variants.length > 0 && (
              <div className="variant-selection-section-modern mb-4">
                <div className="variant-header">
                  <label className="variant-label-modern">Please select a variant.</label>
                </div>
                <div className="variant-options-modern">
                  {product.variants.map((variant) => {
                    // Format variant details for display (e.g., "Size: M, Color: Red")
                    const variantDisplay = variant.variant_detail && Array.isArray(variant.variant_detail)
                      ? variant.variant_detail.map(v => v.option || '').filter(Boolean).join(', ') || variant.sku
                      : variant.sku || `Variant ${variant.id}`;
                    
                    const isSelected = selectedVariant?.id === variant.id;
                    const isOutOfStock = variant.available_stock !== undefined && variant.available_stock === 0 && variant.available_stock !== -1;
                    
                    return (
                  <button
                        key={variant.id}
                        className={`variant-btn-modern ${isSelected ? 'selected' : ''} ${isOutOfStock ? 'out-of-stock' : ''}`}
                        onClick={() => !isOutOfStock && handleVariantSelect(variant)}
                        disabled={isOutOfStock}
                        title={isOutOfStock ? 'Out of stock' : `Select ${variantDisplay}`}
                  >
                        {variantDisplay}
                  </button>
                    );
                  })}
                </div>
                {product.variants.some(v => v.available_stock === 0) && (
                  <a href="#" className="notify-link">Variant not available? Notify Me</a>
                )}
                  </div>
                )}

            {/* Quantity Section */}
            <div className="quantity-section-modern mb-4">
              <label className="quantity-label-modern">Quantity</label>
              <select 
                className="quantity-select-modern"
                value={quantity}
                onChange={(e) => {
                  const newQty = parseInt(e.target.value);
                  if (availableStock === -1 || newQty <= availableStock) {
                    setQuantity(newQty);
                  } else {
                    showWarning(`Only ${availableStock} items available in stock.`);
                  }
                }}
              >
                {Array.from({ length: Math.min(availableStock === -1 ? 10 : availableStock, 10) }, (_, i) => i + 1).map(num => (
                  <option key={num} value={num}>{String(num).padStart(2, '0')}</option>
                ))}
              </select>
            </div>

            {/* Action Buttons - Modern Style */}
            <div className="action-buttons-modern">
              <button
                className={`btn-add-cart-modern ${cartBtn === "REMOVE FROM CART" ? "btn-remove-cart-modern" : ""}`}
                onClick={() => handleCart(product)}
                disabled={availableStock === 0 || (product.variant_enable && product.variants && product.variants.length > 0 && !selectedVariant)}
              >
                {availableStock === 0 ? "OUT OF STOCK" : cartBtn}
              </button>

              <button
                className="btn-wishlist-modern"
                onClick={handleWishlist}
                disabled={loading}
              >
                {wishlistStatus ? (
                  <FaHeart className="wishlist-icon-modern" />
                ) : (
                  <FaRegHeart className="wishlist-icon-modern" />
                )}
                {wishlistStatus ? "REMOVE FROM WISHLIST" : "ADD TO WISHLIST"}
              </button>
            </div>

            {/* Share Section */}
            <div className="share-section-modern">
              <span className="share-label">Share:</span>
              <div className="share-icons">
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} target="_blank" rel="noopener noreferrer" className="share-icon">
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a href={`https://wa.me/?text=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-icon">
                  <i className="fab fa-whatsapp"></i>
                </a>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-icon">
                  <i className="fab fa-twitter"></i>
                </a>
                <a href={`https://www.instagram.com/`} target="_blank" rel="noopener noreferrer" className="share-icon">
                  <i className="fab fa-instagram"></i>
                </a>
              </div>
            </div>

            {/* Delivery Section */}
            <div className="delivery-section-modern">
              <div className="pincode-input-group-modern">
                <input 
                  type="text" 
                  className="pincode-input-modern" 
                  placeholder="Enter Pincode"
                  maxLength="6"
                />
                <button className="btn-check-pincode-modern">CHECK</button>
              </div>
              <p className="delivery-info-modern">
                This product is eligible for return or exchange under our 30-day return or exchange policy. No questions asked.
              </p>
            </div>

            {error && (
              <p className="wishlist-error text-sm text-red-500 mt-2 font-medium">
                {error}
              </p>
            )}
          </div>
        </div>

        {/* Product Details Tabs - Modern Collapsible */}
        <div className="product-details-tabs-modern mt-5">
          <div className="details-accordion">
            <div className="accordion-item-modern">
                  <button
                className={`accordion-header-modern ${activeTab === "details" ? "active" : ""}`}
                onClick={() => setActiveTab(activeTab === "details" ? "" : "details")}
                  >
                <span>Product Details</span>
                <span className="accordion-arrow">{activeTab === "details" ? "−" : "+"}</span>
                  </button>
              {activeTab === "details" && (
                <div className="accordion-content-modern">
                  <div className="product-details-content-modern">
                    <div className="detail-row-modern">
                      <span className="detail-label-modern">Product Name:</span>
                      <span className="detail-value-modern">{product.item_name}</span>
                    </div>
                    <div className="detail-row-modern">
                      <span className="detail-label-modern">Product Code:</span>
                      <span className="detail-value-modern">{product.item_code}</span>
                    </div>
                    <div className="detail-row-modern">
                      <span className="detail-label-modern">Category:</span>
                      <span className="detail-value-modern">{product.cat_name}</span>
                  </div>
                    <div className="detail-row-modern">
                      <span className="detail-label-modern">Unit:</span>
                      <span className="detail-value-modern">{product.unit_name}</span>
                    </div>
                    {product.hsn && (
                      <div className="detail-row-modern">
                        <span className="detail-label-modern">HSN Code:</span>
                        <span className="detail-value-modern">{product.hsn}</span>
                      </div>
                    )}
                  {product.item_note && (
                      <div className="detail-row-modern">
                        <span className="detail-label-modern">Additional Notes:</span>
                        <span className="detail-value-modern">{product.item_note}</span>
                    </div>
                  )}
                  </div>
                </div>
              )}
            </div>

            <div className="accordion-item-modern">
              <button
                className={`accordion-header-modern ${activeTab === "specifications" ? "active" : ""}`}
                onClick={() => setActiveTab(activeTab === "specifications" ? "" : "specifications")}
              >
                <span>Specifications</span>
                <span className="accordion-arrow">{activeTab === "specifications" ? "−" : "+"}</span>
              </button>
              {activeTab === "specifications" && (
                <div className="accordion-content-modern">
                  <div className="product-details-content-modern">
                    <div className="detail-row-modern">
                      <span className="detail-label-modern">Product ID:</span>
                      <span className="detail-value-modern">{product.id}</span>
                    </div>
                    <div className="detail-row-modern">
                      <span className="detail-label-modern">Category ID:</span>
                      <span className="detail-value-modern">{product.category_id}</span>
                    </div>
                    <div className="detail-row-modern">
                      <span className="detail-label-modern">Unit ID:</span>
                      <span className="detail-value-modern">{product.unit_id}</span>
                    </div>
                          {product.sub_category_id && (
                      <div className="detail-row-modern">
                        <span className="detail-label-modern">Sub Category ID:</span>
                        <span className="detail-value-modern">{product.sub_category_id}</span>
                      </div>
                          )}
                    <div className="detail-row-modern">
                      <span className="detail-label-modern">Barcode:</span>
                      <span className="detail-value-modern">{product.bar_code}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SignupModal
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        showOtpLogin={showOtpLogin}
        setShowOtpLogin={setShowOtpLogin}
        handleSignUp={handleSignUp}
        handleSendOtp={handleSendOtp}
        handleVerifyOtp={handleVerifyOtp}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        otp={otp}
        setOtp={setOtp}
        firstName={firstName}
        setFirstName={setFirstName}
        lastName={lastName}
        setLastName={setLastName}
        email={email}
        setEmail={setEmail}
      />
      
      <MessageModal
        isOpen={modalState.isOpen}
        message={modalState.message}
        type={modalState.type}
        onClose={closeModal}
        autoClose={modalState.autoClose}
        autoCloseDelay={modalState.autoCloseDelay}
        showCloseButton={modalState.showCloseButton}
      />
    </div>
  );
};

export default ProductDetail;
